import { useAchievements } from "api/achievements";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { memo, useState } from "react";
import { SelectedFilter } from "types/filters";
import { useStyles } from "../../styles";

interface Props {
  index: number;
  size: "small" | "medium" | "inline";
  onChange?: () => void;
  filter: SelectedFilter;
}

export const AchievementName = memo(
  ({ index, size, onChange, filter }: Props) => {
    const classes = useStyles();
    const { message } = useMessage();
    const isInlineType = size === "inline";
    const [achievementSearch, setAchievementSearch] = useState("");
    const { useInfiniteAchievmentsList } = useAchievements();

    const {
      data: infiniteAchievementsList,
      size: achievementsSize,
      setSize: setAchievementsSize,
      isValidating,
    } = useInfiniteAchievmentsList({
      filters: achievementSearch
        ? [
            {
              condition: "like",
              query: "name",
              value: achievementSearch,
            },
          ]
        : [],
      order: "asc",
      sortKey: "name",
    });

    const {
      infiniteData: achievementsList,
      anyElementsToFetch: anyAchievementsToFetch,
    } = useManageInfiniteScroll(infiniteAchievementsList || []);

    return filter.condition === "eq" ? (
      <SelectInput
        name={`filters.${index}.value`}
        displaySearch
        placeholder={message({ id: "value" })}
        items={achievementsList.map(({ achievementId, name }) => ({
          label: name,
          id: achievementId,
        }))}
        asyncSearch={(searchedQuery: string) =>
          setAchievementSearch(searchedQuery)
        }
        data-test-id="filter-value"
        className={isInlineType ? classes.inlineValue : ""}
        data-hj-suppress
        adjustWidthToValue={isInlineType}
        anyElementsToFetch={anyAchievementsToFetch}
        loadMoreElements={() =>
          !isValidating && setAchievementsSize(achievementsSize + 1)
        }
        size={isInlineType ? "small" : size}
        onChange={onChange}
        keepDetailedValue
      />
    ) : (
      <TextInput
        className={isInlineType ? classes.inlineValue : ""}
        size={isInlineType ? "small" : size}
        placeholder={message({ id: "value" })}
        name={`filters.${index}.value`}
        data-test-id="filter-value"
        data-hj-suppress
        adjustWidthToValue={isInlineType}
        customOnBlur={() => {
          if (isInlineType && onChange) {
            onChange();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onChange?.();
          }
        }}
      />
    );
  },
);
